import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { HeartHandshake, CalendarClock, Truck, MapPin, Cross, Handshake, MessagesSquare, Puzzle, Users, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import RoadLine from "../components/RoadLine";
import FinalCTA from "../components/FinalCTA";
import ParallaxImage from "../components/ParallaxImage";
import { IMAGES } from "../content/company";

const HERO_STATS = [
    { icon: CalendarClock, value: "15", label: "Years", caption: "of experience", testId: "hero-stat-years" },
    { icon: Truck, value: "20+", label: "Trucks", caption: "on the road", testId: "hero-stat-trucks" },
    { icon: Users, value: "Family", label: "Owned", caption: "built on values", testId: "hero-stat-family" },
];

const SNAPSHOT = [
    { icon: HeartHandshake, title: "Family Owned", text: "Built on relationships and trust.", testId: "snapshot-family" },
    { icon: CalendarClock, title: "Nearly 15 Years", text: "Experience in the trucking industry.", testId: "snapshot-years" },
    { icon: Truck, title: "Around 20 Trucks", text: "Growing without losing the personal connection.", testId: "snapshot-fleet" },
    { icon: MapPin, title: "Rochester, NY", text: "Proudly based in Western New York.", testId: "snapshot-rochester" },
    { icon: Cross, title: "Faith Driven", text: "Proverbs 16:3 — “Commit to the LORD whatever you do, and he will establish your plans.”", testId: "snapshot-faith" },
];

const VALUES = [
    { num: "01", icon: Handshake, title: "Respect", text: "Drivers deserve to be treated like professionals and like people. We believe mutual respect is the foundation of a strong company.", testId: "value-respect" },
    { num: "02", icon: MessagesSquare, title: "Communication", text: "People should be able to talk to each other. We work to keep communication straightforward, accessible, and personal.", testId: "value-communication" },
    { num: "03", icon: Puzzle, title: "Solutions", text: "Not every situation is one-size-fits-all. We try to find reasonable solutions that work for both our drivers and the company.", testId: "value-solutions" },
    { num: "04", icon: Users, title: "Relationships", text: "Even as we grow, we want Nightline to remain a company where people know each other and drivers feel heard.", testId: "value-relationships" },
];

const FLEET = [
    { src: IMAGES.heroSunset, fallback: IMAGES.heroSunsetFallback, alt: "White Nightline Express semi-truck on an open American highway at sunset", caption: "On the road" },
    { src: IMAGES.yard, fallback: IMAGES.yardFallback, alt: "Clean white Nightline tractor and trailer at a loading facility", caption: "At the dock" },
    { src: IMAGES.cityRun, fallback: IMAGES.cityRunFallback, alt: "Nightline Express truck and trailer running the highway into the city at dusk", caption: "Into the city" },
    { src: IMAGES.dusk, fallback: IMAGES.duskFallback, alt: "White Nightline Express semi-truck with headlights on, running the highway at dusk", caption: "Night shift" },
];

const Home = () => {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
    const heroBgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
    const heroFade = useTransform(scrollYProgress, [0, 0.65], [1, 0]);

    return (
    <>
        <Seo
            title="Nightline Express Inc | Family-Owned Trucking Company in Rochester, NY"
            description="Nightline Express Inc is a family-owned trucking company in Rochester, New York with nearly 15 years of experience and around 20 trucks. Drivers are people, not truck numbers."
        />

        {/* HERO */}
        <section ref={heroRef} data-testid="home-hero" className="relative flex min-h-[100svh] items-center overflow-hidden bg-night">
            <motion.div style={{ y: heroBgY }} className="absolute inset-0 scale-110 will-change-transform">
                <picture>
                    <source
                        srcSet="/images/truck-dusk-828.webp 828w, /images/truck-dusk-1200.webp 1200w, /images/truck-dusk-1534.webp 1534w"
                        sizes="100vw"
                        type="image/webp"
                    />
                    <img
                        src={IMAGES.duskFallback}
                        alt="New white Freightliner Cascadia from the Nightline Express fleet running the highway at dusk with headlights on"
                        width="1534"
                        height="1025"
                        className="h-full w-full object-cover object-[68%_center]"
                        fetchPriority="high"
                        decoding="async"
                    />
                </picture>
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-r from-night via-night/75 to-night/20" aria-hidden="true" />
            <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-night to-transparent" aria-hidden="true" />

            <motion.div style={{ opacity: heroFade }} className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 pb-72 pt-36 sm:pb-64">
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="flex items-center gap-3 font-heading text-xs font-semibold uppercase tracking-[0.3em] text-brand"
                >
                    <span className="inline-block h-px w-10 bg-brand" aria-hidden="true" />
                    Family-Owned · Rochester, NY
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.25 }}
                    className="mt-6 font-heading text-5xl font-extrabold uppercase leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl"
                >
                    <span className="block text-white">Driven</span>
                    <span className="block text-brand">By Faith.</span>
                </motion.h1>
                <motion.p
                    initial={{ opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="mt-6 max-w-md text-base leading-relaxed text-white/80 md:text-lg"
                >
                    Nearly 15 years on the road, built around the people behind the wheel.
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 28 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.55 }}
                    className="mt-9 flex flex-col items-start gap-6"
                >
                    <Link
                        to="/drivers#apply"
                        data-testid="hero-drive-button"
                        className="inline-flex items-center gap-2 rounded-md bg-brand px-7 py-4 font-heading text-sm font-bold uppercase tracking-wider text-night transition-[background-color,transform] duration-200 hover:bg-brand-dark hover:-translate-y-0.5"
                    >
                        Drive With Nightline
                        <ArrowRight size={17} />
                    </Link>
                    <Link
                        to="/about"
                        data-testid="hero-learn-link"
                        className="group inline-flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-wider text-white underline decoration-brand decoration-2 underline-offset-8 transition-colors duration-200 hover:text-brand"
                    >
                        Learn About Us
                        <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                </motion.div>
            </motion.div>

            <div className="absolute inset-x-0 bottom-0">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 pb-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75 }}
                        data-testid="hero-stats-panel"
                        className="grid grid-cols-3 divide-x divide-white/10 rounded-md border border-white/10 bg-night-deep/85 backdrop-blur-md"
                    >
                        {HERO_STATS.map((s) => (
                            <div key={s.testId} data-testid={s.testId} className="flex flex-col items-center gap-2 px-3 py-5 text-center sm:flex-row sm:gap-4 sm:px-8 sm:py-6 sm:text-left">
                                <s.icon size={26} strokeWidth={1.5} className="shrink-0 text-brand" />
                                <div>
                                    <p className="font-heading text-xl font-extrabold uppercase leading-tight text-white sm:text-2xl">
                                        {s.value} <span className="text-sm font-bold tracking-widest sm:text-base">{s.label}</span>
                                    </p>
                                    <p className="mt-1 text-xs text-white/60">{s.caption}</p>
                                </div>
                            </div>
                        ))}
                    </motion.div>
                </div>
                <RoadLine testId="hero-road-line" className="opacity-80" />
            </div>
        </section>

        {/* TRUST / SNAPSHOT BAR */}
        <section data-testid="snapshot-bar" className="border-b border-night/10 bg-white">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-px sm:grid-cols-2 lg:grid-cols-5">
                {SNAPSHOT.map((s, i) => (
                    <Reveal key={s.testId} delay={i * 0.08}>
                        <div data-testid={s.testId} className="flex h-full items-start gap-4 px-6 py-8 sm:px-8">
                            <s.icon size={26} strokeWidth={1.5} className="mt-1 shrink-0 text-brand" />
                            <div>
                                <h2 className="font-heading text-lg font-semibold text-night">{s.title}</h2>
                                <p className="mt-1 text-sm leading-relaxed text-night/60">{s.text}</p>
                            </div>
                        </div>
                    </Reveal>
                ))}
            </div>
        </section>

        {/* INTRODUCTION */}
        <section data-testid="intro-section" className="relative overflow-hidden bg-white py-24 md:py-32">
            <motion.span
                aria-hidden="true"
                animate={{ y: [0, -16, 0] }}
                transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
                className="text-outline-night pointer-events-none absolute -right-8 top-10 select-none font-heading text-[9rem] font-extrabold leading-none md:text-[15rem]"
            >
                15
            </motion.span>
            <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 md:grid-cols-12">
                <Reveal className="md:col-span-5">
                    <ParallaxImage
                        testId="intro-image"
                        webp={IMAGES.yard}
                        src={IMAGES.yardFallback}
                        alt="White Nightline Express tractor and trailer at a loading facility"
                        imgClassName="aspect-[4/5] md:aspect-[3/4]"
                    />
                </Reveal>
                <div className="md:col-span-7 lg:pl-8">
                    <Reveal>
                        <p className="flex items-center gap-3 font-heading text-xs font-semibold uppercase tracking-[0.3em] text-brand-ink">
                            <span className="inline-block h-px w-10 bg-brand" aria-hidden="true" />
                            Who We Are
                        </p>
                        <h2 className="mt-5 font-heading text-3xl font-bold leading-tight tracking-tight text-night sm:text-4xl lg:text-5xl">
                            A trucking company built around people.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <div className="mt-6 space-y-5 text-base leading-relaxed text-night/70">
                            <p>
                                Nightline Express is a family-owned trucking company based in Rochester,
                                New York. For nearly 15 years, we've built our business around a simple
                                idea: treat people right, communicate honestly, and do what you say you're
                                going to do.
                            </p>
                            <p>
                                What started as a family business has grown into a fleet of around 20
                                trucks, but we have never wanted to lose the personal relationships that
                                got us here.
                            </p>
                            <p>
                                For our customers, that means dependable people they can trust. For our
                                drivers, it means being treated like a person, not a truck number.
                            </p>
                        </div>
                        <Link
                            to="/about"
                            data-testid="intro-story-link"
                            className="group mt-8 inline-flex items-center gap-2 font-heading text-base font-semibold text-night transition-colors hover:text-brand-ink"
                        >
                            Our Story
                            <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </div>
        </section>

        {/* SIGNATURE BRAND SECTION */}
        <section data-testid="signature-section" className="relative overflow-hidden bg-night py-28 md:py-40">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal>
                    <p className="flex items-center gap-3 font-heading text-xs font-semibold uppercase tracking-[0.3em] text-brand">
                        <span className="inline-block h-px w-10 bg-brand" aria-hidden="true" />
                        The Nightline Difference
                    </p>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="mt-8 font-heading font-extrabold uppercase leading-[1.02] tracking-tight">
                        <span className="block text-4xl text-white sm:text-6xl lg:text-7xl">Drivers Are People.</span>
                        <span className="text-outline mt-2 block text-4xl sm:text-6xl lg:text-7xl">Not Truck Numbers.</span>
                    </h2>
                </Reveal>
                <Reveal delay={0.2}>
                    <div className="mt-10 max-w-md">
                        <RoadLine testId="signature-road-line" />
                    </div>
                    <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg">
                        We believe a trucking company is only as strong as the drivers behind it. That's
                        why respect, communication, and finding solutions that work for both the driver
                        and the company are at the heart of how Nightline operates.
                    </p>
                    <Link
                        to="/drivers"
                        data-testid="signature-cta"
                        className="group mt-10 inline-flex items-center gap-2 rounded-md border border-brand/60 px-7 py-3.5 font-heading text-sm font-semibold uppercase tracking-widest text-brand transition-colors duration-200 hover:bg-brand hover:text-night"
                    >
                        Why Drivers Choose Nightline
                        <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                    </Link>
                </Reveal>
            </div>
        </section>

        {/* WHY NIGHTLINE */}
        <section data-testid="why-nightline-section" className="bg-surface py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal>
                    <p className="flex items-center gap-3 font-heading text-xs font-semibold uppercase tracking-[0.3em] text-brand-ink">
                        <span className="inline-block h-px w-10 bg-brand" aria-hidden="true" />
                        Why Nightline
                    </p>
                    <h2 className="mt-5 max-w-2xl font-heading text-3xl font-bold leading-tight tracking-tight text-night sm:text-4xl lg:text-5xl">
                        Built differently from the driver's seat up.
                    </h2>
                </Reveal>
                <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                    {VALUES.map((v, i) => (
                        <Reveal key={v.testId} delay={i * 0.09}>
                            <article
                                data-testid={v.testId}
                                className="flex h-full flex-col rounded-md border border-night/10 bg-white p-7 shadow-sm transition-[box-shadow,transform] duration-300 hover:shadow-lg hover:-translate-y-1"
                            >
                                <div className="flex items-center justify-between">
                                    <v.icon size={26} strokeWidth={1.5} className="text-brand-ink" />
                                    <span className="font-heading text-sm font-bold tracking-widest text-night/25">{v.num}</span>
                                </div>
                                <h3 className="mt-6 font-heading text-xl font-semibold text-night">{v.title}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-night/60">{v.text}</p>
                                <span className="mt-auto pt-6">
                                    <span className="block h-0.5 w-10 bg-brand" aria-hidden="true" />
                                </span>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        {/* CUSTOMER / FREIGHT */}
        <section data-testid="customer-section" className="bg-white py-24 md:py-32">
            <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 md:grid-cols-12">
                <div className="order-2 md:order-1 md:col-span-6 lg:pr-8">
                    <Reveal>
                        <p className="flex items-center gap-3 font-heading text-xs font-semibold uppercase tracking-[0.3em] text-brand-ink">
                            <span className="inline-block h-px w-10 bg-brand" aria-hidden="true" />
                            For Our Customers
                        </p>
                        <h2 className="mt-5 font-heading text-3xl font-bold leading-tight tracking-tight text-night sm:text-4xl lg:text-5xl">
                            Reliable transportation starts with reliable people.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <div className="mt-6 space-y-5 text-base leading-relaxed text-night/70">
                            <p>
                                Moving freight requires more than trucks. It takes communication,
                                accountability, experience, and people who take their responsibilities
                                seriously.
                            </p>
                            <p>
                                Nightline Express brings nearly 15 years of trucking experience to every
                                customer relationship. Our goal is simple: build long-term relationships
                                by being a transportation company our customers know they can depend on.
                            </p>
                        </div>
                        <Link
                            to="/contact"
                            data-testid="customer-cta"
                            className="mt-9 inline-block rounded-md bg-night px-8 py-4 font-heading text-base font-semibold text-white transition-[background-color,transform] duration-200 hover:bg-night-soft hover:-translate-y-0.5"
                        >
                            Work With Nightline
                        </Link>
                    </Reveal>
                </div>
                <Reveal className="order-1 md:order-2 md:col-span-6">
                    <ParallaxImage
                        testId="customer-image"
                        webp={IMAGES.cityRun}
                        src={IMAGES.cityRunFallback}
                        alt="Nightline Express truck and trailer moving freight on the highway at dusk"
                        imgClassName="aspect-[4/3]"
                    />
                </Reveal>
            </div>
        </section>

        {/* FLEET */}
        <section data-testid="fleet-section" className="bg-surface py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Reveal>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <p className="flex items-center gap-3 font-heading text-xs font-semibold uppercase tracking-[0.3em] text-brand-ink">
                                <span className="inline-block h-px w-10 bg-brand" aria-hidden="true" />
                                Our Equipment
                            </p>
                            <h2 className="mt-5 font-heading text-3xl font-bold leading-tight tracking-tight text-night sm:text-4xl lg:text-5xl">
                                The Nightline Fleet
                            </h2>
                        </div>
                        <p className="max-w-sm text-sm leading-relaxed text-night/60">
                            A look at the clean white equipment our drivers take pride in, out on real
                            American highways.
                        </p>
                    </div>
                </Reveal>
                <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
                    {FLEET.map((f, i) => (
                        <Reveal key={f.caption} delay={i * 0.09}>
                            <figure data-testid={`fleet-photo-${i + 1}`} className="group overflow-hidden rounded-md border border-night/10 bg-white">
                                <div className="overflow-hidden">
                                    <picture>
                                        <source srcSet={f.src} type="image/webp" />
                                        <img
                                            src={f.fallback}
                                            alt={f.alt}
                                            loading="lazy"
                                            className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                    </picture>
                                </div>
                                <figcaption className="flex items-center justify-between px-5 py-4">
                                    <span className="font-heading text-sm font-semibold text-night">{f.caption}</span>
                                    <span className="h-0.5 w-8 bg-brand" aria-hidden="true" />
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        <FinalCTA />
    </>
    );
};

export default Home;
